import React from "react";
import { CpColorPicker } from "@components";

type ColorFormat = "hex" | "rgb" | "hsl";

type LegacyEvent<T> = {
  detail: T;
};

type LegacyEventCallback<T> = (event: LegacyEvent<T>) => void;

type CprColorpickerProps = {
  /**
   * The color to be displayed in the colorpicker.
   * Any valid color format is supported.
   */
  color?: string;
  /**
   * The format for the data in the colorchange event.
   * Defaults to "hex".
   */
  format?: ColorFormat;
  /**
   * Whether to show only shades of the current color (monochromatic mode)
   * or the full color wheel. Defaults to false.
   */
  monochromatic?: boolean;
  /**
   * Legacy event handlers for backward compatibility.
   */
  events?: {
    /**
     * Fired when the color changes (normal mode).
     * Receives an event object with detail containing the color string.
     */
    colorchange?: LegacyEventCallback<string>;
    /**
     * Fired when the lightness changes (monochromatic mode).
     * Receives an event object with detail containing the lightness value (0-100).
     */
    lightnesschange?: LegacyEventCallback<number>;
  };
};

/**
 * Legacy CprColorpicker component - now a wrapper around the modern CpColorPicker.
 *
 * This maintains backward compatibility with the custom element events pattern
 * while using the modern component implementation internally.
 *
 * @deprecated Use CpColorPicker directly for new implementations
 *
 * Legacy API:
 * - Props: color, format, monochromatic, events
 * - Events: events.colorchange({ detail: colorString })
 *           events.lightnesschange({ detail: lightnessNumber })
 *
 * Modern API (CpColorPicker):
 * - Props: color, format, monochromatic, onColorChange, onLightnessChange
 * - Callbacks: onColorChange(colorString), onLightnessChange(lightnessNumber)
 */
export class CprColorpicker extends React.Component<CprColorpickerProps> {
  render() {
    const { color, format, monochromatic, events } = this.props;

    return (
      <CpColorPicker
        color={color}
        format={format}
        monochromatic={monochromatic}
        onColorChange={
          events?.colorchange
            ? (colorValue) => {
                // Wrap the color value in the legacy event structure
                events.colorchange?.({ detail: colorValue });
              }
            : undefined
        }
        onLightnessChange={
          events?.lightnesschange
            ? (lightnessValue) => {
                // Wrap the lightness value in the legacy event structure
                events.lightnesschange?.({ detail: lightnessValue });
              }
            : undefined
        }
      />
    );
  }
}
